/**
 * DateRangePicker Component
 * Date range selector with preset options
 */

import { useState, useMemo } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import { DateRange } from '@/types'
import clsx from 'clsx'

interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
  className?: string
}

const PRESETS: Array<{ label: string; days: number }> = [
  { label: 'Last 24 hours', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

function getDateRange(days: number): DateRange {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - days)

  const preset = PRESETS.find(p => p.days === days)

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    label: preset?.label || `Last ${days} days`
  }
}

export default function DateRangePicker({
  value,
  onChange,
  className
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [customMode, setCustomMode] = useState(false)
  const [customStart, setCustomStart] = useState(value.start.split('T')[0])
  const [customEnd, setCustomEnd] = useState(value.end.split('T')[0])

  const handlePresetSelect = (days: number) => {
    const range = getDateRange(days)
    onChange(range)
    setIsOpen(false)
    setCustomMode(false)
  }

  const handleCustomApply = () => {
    const range: DateRange = {
      start: new Date(customStart).toISOString(),
      end: new Date(customEnd).toISOString(),
      label: 'Custom range'
    }
    onChange(range)
    setIsOpen(false)
  }

  const currentPreset = useMemo(() => {
    return PRESETS.find(p => p.label === value.label)
  }, [value.label])

  return (
    <div className={clsx('relative', className)}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-surface-800 border border-surface-600 rounded-lg hover:border-surface-500 transition-colors w-full"
      >
        <Calendar size={14} className="text-surface-400" />
        <span className="flex-1 text-left text-sm text-surface-200">
          {value.label || `${formatDate(value.start)} - ${formatDate(value.end)}`}
        </span>
        <ChevronDown
          size={14}
          className={clsx(
            'text-surface-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-20 overflow-hidden">
            {/* Presets */}
            {!customMode && (
              <div className="p-2">
                <p className="text-xs text-surface-400 uppercase px-2 mb-2">Quick Select</p>
                <div className="space-y-1">
                  {PRESETS.map(preset => (
                    <button
                      key={preset.days}
                      onClick={() => handlePresetSelect(preset.days)}
                      className={clsx(
                        'w-full px-3 py-2 text-sm text-left rounded transition-colors',
                        currentPreset?.days === preset.days
                          ? 'bg-primary-500/20 text-primary-400'
                          : 'hover:bg-surface-700 text-surface-200'
                      )}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <div className="border-t border-surface-700 mt-2 pt-2">
                  <button
                    onClick={() => setCustomMode(true)}
                    className="w-full px-3 py-2 text-sm text-left rounded hover:bg-surface-700 text-surface-200"
                  >
                    Custom range...
                  </button>
                </div>
              </div>
            )}

            {/* Custom Date Selection */}
            {customMode && (
              <div className="p-3">
                <button
                  onClick={() => setCustomMode(false)}
                  className="text-xs text-primary-400 hover:text-primary-300 mb-3"
                >
                  ← Back to presets
                </button>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-surface-400 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm bg-surface-900 border border-surface-600 rounded focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-surface-400 mb-1">End Date</label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm bg-surface-900 border border-surface-600 rounded focus:border-primary-500 focus:outline-none"
                    />
                  </div>

                  <button
                    onClick={handleCustomApply}
                    className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm rounded transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
